import {ExternalLinkIcon} from '@heroicons/react/outline';
import type {JSX} from 'react';

import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';

type TransactionDocumentsPanelProps = {
  data: AfriexBusinessTransaction;
};

/**
 * Documents attached to a merchant transaction, shown as the Documents tab of
 * the transaction detail. Documents are never exposed as raw S3 keys — the
 * server decorates short-lived presigned GET URLs (`signedInvoice`) onto the
 * detail response, and this panel only renders those.
 *
 * Today: the per-transaction SWIFT invoice. Future document types (settlement
 * advices, etc.) should be listed here the same way.
 */
export default function TransactionDocumentsPanel({
  data,
}: TransactionDocumentsPanelProps): JSX.Element {
  if (!data.signedInvoice) {
    return (
      <p
        className="text-sm text-gray-500 dark:text-gray-300 py-2"
        data-cy="merchant-transaction-documents-empty"
      >
        No documents available for this transaction.
      </p>
    );
  }

  return (
    <div className="w-full" data-cy="merchant-transaction-documents">
      <div className="flex justify-between py-2 border-b dark:border-dark-borderMid last:border-0">
        <p className="text-slate-500 text-sm dark:text-slate-100">
          Transaction Invoice
        </p>
        <div className="w-1/2">
          <a
            href={data.signedInvoice}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm flex gap-1 text-cyan-700 hover:underline w-fit"
          >
            <span>View invoice</span>
            <ExternalLinkIcon className="w-4 h-4 self-center" />
          </a>
        </div>
      </div>
    </div>
  );
}
